import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from './../../kuba.services';
import { KubaGeneralService } from './../../kuba-general.services';
import { Router, ActivatedRoute } from '@angular/router';
import { SelectItem, MessageService } from 'primeng/api';
import {
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl
} from '@angular/forms';
import {
  inject,
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { ManualView, ManualEdit } from './../models/index';
import { ManualService } from './../services/manual.services';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'manual-form',
  templateUrl: 'manual-form.component.html',
  standalone: false
})
export class ManualFormComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);

  @Input() manualType: string;

  @Input() manual = new ManualView();

  @Output() onClose = new EventEmitter<any>();
  @Output() buttonStateChange = new EventEmitter<{
    showSaveAndClose: boolean;
    hideSaveandcontinue: boolean;
  }>();

  allManualList: ManualView[];
  createManualForm: FormGroup;
  status: SelectItem[];
  chooseBusiness: SelectItem[];
  choosePortal: SelectItem[];
  showUpload = false;
  uploadStartForCoverImage: boolean;
  public selectedFiles: any;
  coverImage: string;
  loading: boolean;
  public hideSaveandcontinue = false;
  public showSaveAndClose = true;
  public readonly Validators = Validators;

  private subscriptions: Subscription[] = [];
  private cdr: ChangeDetectorRef;

  /**
   * Initializes services, form builder, and sets up initial form structure and status dropdown options.
   * @param kubaGeneralService - Service for general Kuba application data (like portal/business lists).
   * @param formBuilder - Angular's FormBuilder service for creating reactive forms.
   * @param router - Angular's Router service for navigation.
   * @param route - Angular's ActivatedRoute service for accessing route parameters.
   * @param manualService - Service for manual-specific API calls.
   * @param http - Angular's HttpClient for making HTTP requests (used for file upload).
   * @param translate - ngx-translate service for handling internationalization.
   */
  constructor(
    private kubaGeneralService: KubaGeneralService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private manualService: ManualService,
    private http: HttpClient,
    private translate: TranslateService,
    private messageService: MessageService, // Inject MessageService
    cdr: ChangeDetectorRef
  ) {
    this.cdr = cdr; // Assign it

    // Subscribe to language changes for the Status dropdown
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUS_DROP').subscribe(val => {
        this.status = []; // Initialize/clear the array
        this.status.push(
          { label: val.SELECT, value: null },
          { label: val.INACTIVE, value: 2 },
          { label: val.ACTIVE, value: 1 }
        );
      })
    );

    // Initialize the form with default structure and validators
    // This initial structure might be overridden in ngOnInit based on manualType
    this.createManualForm = this.formBuilder.group({
      title: [
        '', // Default value
        Validators.compose([Validators.required, this.noWhitespaceValidator]) // Validators
      ],
      description: [''],
      status: [1, Validators.required], // Default status to Active (value 1)
      business: [null],
      portal: [null],
      coverImg: [''] // Not directly used by form controls, handled separately
    });
  }
  /**
   * Lifecycle hook called after component initialization.
   * Sets up form validators based on manualType, fetches dropdown data (Business/Portal),
   * and populates the form if editing an existing manual.
   */
  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    this.emitButtonState();

    // --- Re-configure form validators based on manualType ---
    if (this.manualType === 'business') {
      this.createManualForm = this.formBuilder.group({
        title: [
          '',
          Validators.compose([Validators.required, this.noWhitespaceValidator])
        ],
        description: [''],
        status: [1, Validators.required],
        business: [null, Validators.required],
        portal: [null],
        coverImg: ['']
      });
    } else if (this.manualType === 'portal') {
      this.createManualForm = this.formBuilder.group({
        title: [
          '',
          Validators.compose([Validators.required, this.noWhitespaceValidator])
        ],
        description: [''],
        status: [1, Validators.required],
        business: [null],
        portal: [null, Validators.required],
        coverImg: ['']
      });
    } else {
      this.createManualForm = this.formBuilder.group({
        title: [
          '',
          Validators.compose([Validators.required, this.noWhitespaceValidator])
        ],
        description: [''],
        status: [1, Validators.required],
        business: [null],
        portal: [null],
        coverImg: ['']
      });
    }

    // --- Fetch Business List for Dropdown ---
    this.chooseBusiness = []; // Initialize
    // Get the translated "Select" label ONCE
    this.subscriptions.push(
      // Add subscription for the translate stream
      this.translate
        .stream('SELECT_BUSINESS_DROP')
        .pipe(take(1)) // Use take(1)
        .subscribe(val => {
          // Check if default option already exists before adding
          if (!this.chooseBusiness.some(option => option.value === null)) {
            this.chooseBusiness.unshift({
              // Add default option to the beginning
              label: val.SELECT_BUSINESS,
              value: null
            });
            // Use setTimeout for change detection here as well if issues persist
            setTimeout(() => this.cdr.detectChanges(), 0);
          }
        })
    );
    // Fetch the actual business list (separate subscription)
    this.subscriptions.push(
      // Add subscription for the business list fetch
      this.kubaGeneralService.getBusinessList().subscribe((result: any) => {
        const currentOptions = new Set(
          this.chooseBusiness.map(opt => opt.value)
        );
        result.forEach((element: { Name: any; Id: any }) => {
          // Add only if not already present (handles potential multiple emissions)
          if (!currentOptions.has(element.Id)) {
            this.chooseBusiness.push({
              label: element.Name,
              value: element.Id
            });
            currentOptions.add(element.Id); // Update the set
          }
        });
        // Sort keeping null at top
        this.chooseBusiness.sort((a, b) =>
          a.value === null
            ? -1
            : b.value === null
              ? 1
              : String(a.label).localeCompare(String(b.label))
        );
        // Use setTimeout for change detection here as well if issues persist
        setTimeout(() => this.cdr.detectChanges(), 0);
      })
    );

    // --- Fetch Portal List for Dropdown ---
    this.choosePortal = []; // Initialize
    // Get the translated "Select" label ONCE
    this.subscriptions.push(
      // Add subscription for the translate stream
      this.translate
        .stream('SELECT_PORTAL_DROP')
        .pipe(take(1)) // Use take(1)
        .subscribe(val => {
          // Check if default option already exists before adding
          if (!this.choosePortal.some(option => option.value === null)) {
            this.choosePortal.unshift({
              // Add default option to the beginning
              label: val.SELECT_PORTAL,
              value: null
            });
            // Use setTimeout for change detection here as well if issues persist
            setTimeout(() => this.cdr.detectChanges(), 0);
          }
        })
    );
    // Fetch the actual portal list (separate subscription)
    this.subscriptions.push(
      // Add subscription for the portal list fetch
      this.kubaGeneralService.getPortalList().subscribe((result: any) => {
        const currentOptions = new Set(this.choosePortal.map(opt => opt.value));
        result.forEach((element: { Name: any; Id: any }) => {
          // Add only if not already present
          if (!currentOptions.has(element.Id)) {
            this.choosePortal.push({
              label: element.Name,
              value: element.Id
            });
            currentOptions.add(element.Id);
          }
        });
        // Sort keeping null at top
        this.choosePortal.sort((a, b) =>
          a.value === null
            ? -1
            : b.value === null
              ? 1
              : String(a.label).localeCompare(String(b.label))
        );
        // Use setTimeout for change detection here as well if issues persist
        setTimeout(() => this.cdr.detectChanges(), 0);
      })
    );

    // --- Populate Form for Editing ---
    if (this.manual && this.manual.Id !== 0) {
      this.showUpload = true;
      let manualEdit = {
        title: this.manual.Title,
        description: this.manual.Description,
        status: this.manual.Status,
        business: this.manual.LevelId === 3 ? this.manual.LevelKeyId : null,
        portal: this.manual.LevelId === 2 ? this.manual.LevelKeyId : null,
        coverImg: this.manual.CoverImg
      };
      this.coverImage = this.manual.CoverImg;
      this.createManualForm.patchValue(manualEdit);

      // Disable controls after patching
      if (
        this.manualType === 'business' &&
        this.createManualForm.get('business')
      ) {
        this.createManualForm.get('business')?.disable({ emitEvent: false });
      }
      if (this.manualType === 'portal' && this.createManualForm.get('portal')) {
        this.createManualForm.get('portal')?.disable({ emitEvent: false });
      }
      // Use setTimeout for change detection here as well if issues persist
      setTimeout(() => this.cdr.detectChanges(), 0);
    } else {
      // Ensure controls are enabled for new manuals
      this.createManualForm.get('title')?.enable({ emitEvent: false });
      this.createManualForm.get('business')?.enable({ emitEvent: false });
      this.createManualForm.get('portal')?.enable({ emitEvent: false });
    }
  }

  /**
   * Emits the onClose event with a payload indicating the form was not saved (canceled).
   * This method is called internally or triggered by the parent component's cancel action.
   */
  cancelClose() {
    // Use the closePopUp helper to structure the emitted object
    this.onClose.emit(this.closePopUp(false)); // isSaved = false
  }

  /**
   * Initiates the process of saving the manual data.
   * Performs validation checks (including checking for duplicate titles and feature enablement)
   * before calling the actual save logic in saveManualDetails.
   * This method is triggered by the parent component's save action.
   * @param proceed - Optional boolean indicating if the user intends to save and navigate immediately ('Save & Continue').
   */
  saveManual(proceed?: boolean) {
    // Get all manuals to check for duplicate titles
    this.subscriptions.push(
      this.manualService.getAllManuallist(BaseServices.ApplicationId).subscribe(
        result => {
          if (result) {
            this.allManualList = result;
            let manualList = this.allManualList;
            // Use getRawValue() to include disabled control values (like title when editing)
            const currentFormValues = this.createManualForm.getRawValue();
            const newTitle = currentFormValues.title.trim(); // Trim whitespace for comparison

            // Filter existing manuals for a matching title, excluding the current manual if editing
            let existingData = manualList.filter(
              x =>
                x.Title.trim().toLowerCase() === newTitle.toLowerCase() &&
                x.Id !== this.manual.Id
            );

            // Check for duplicates only if the title exists and is non-empty
            if (newTitle && existingData.length > 0) {
              // Use translate service for alert message
              this.translate
                .get('MANUAL_NAME_ALREADY_EXIST')
                .subscribe((res: string) => {
                  this.notifications.add({ severity: 'warn', detail: res });
                });
            } else {
              // Proceed with feature checks and saving if title is unique or unchanged/empty
              let manualSave = currentFormValues; // Use raw values which include potentially disabled fields

              // Check feature enablement if business or portal is selected
              if (manualSave.business) {
                this.subscriptions.push(
                  this.manualService
                    .getBusinessFeatureManual(manualSave.business)
                    .subscribe(featureResult => {
                      if (featureResult === 'false') {
                        // Use translate service for alert message
                        this.translate
                          .get('MANUAL_FEATURE_NOT_ENABLED')
                          .subscribe((res: string) => {
                            this.notifications.add({ severity: 'warn', detail: res }); // Or use a more user-friendly notification
                          });
                      } else {
                        this.saveManualDetails(proceed); // Feature enabled, proceed to save
                      }
                    })
                );
              } else if (manualSave.portal) {
                this.subscriptions.push(
                  this.manualService
                    .getPortalFeatureManual(manualSave.portal)
                    .subscribe(featureResult => {
                      if (featureResult === 'false') {
                        // Use translate service for alert message
                        this.translate
                          .get('MANUAL_FEATURE_NOT_ENABLED')
                          .subscribe((res: string) => {
                            this.notifications.add({ severity: 'warn', detail: res }); // Or use a more user-friendly notification
                          });
                      } else {
                        this.saveManualDetails(proceed); // Feature enabled, proceed to save
                      }
                    })
                );
              } else {
                // No business or portal selected (common manual), save directly
                this.saveManualDetails(proceed);
              }
            }
          } else {
            // Handle case where getAllManuallist returns null/undefined
            console.error('Could not retrieve manual list for validation.');
            // Optionally alert the user or log appropriately
          }
        },
        error => {
          console.error('Error fetching manual list for validation:', error);
          // Optionally alert the user
        }
      )
    );
  }

  /**
   * Performs the actual saving (add or update) of the manual data via the ManualService.
   * This method is called by `saveManual` after preliminary checks have passed.
   * @param proceed - Optional boolean passed from `saveManual`, indicating if the user intends to save and continue.
   */
  saveManualDetails(proceed?: boolean) {
    let manualEdit = new ManualEdit();
    // Use getRawValue() to ensure values from disabled controls (like title/business/portal during edit) are included
    let manualSave = this.createManualForm.getRawValue();

    // Populate the ManualEdit object from the existing manual and form data
    manualEdit.Id = this.manual.Id;
    manualEdit.CreatedOn = this.manual.CreatedOn || null!; // Use existing or null
    manualEdit.ModifiedOn = this.manual.ModifiedOn || null!; // Use existing or null
    manualEdit.CreatedBy = this.manual.CreatedBy || null!; // Use existing or null
    manualEdit.ModifiedBy = this.manual.ModifiedBy || null!; // Use existing or null
    manualEdit.Title = manualSave.title.trim(); // Trim whitespace
    manualEdit.Description = manualSave.description
      ? manualSave.description.trim()
      : ''; // Trim or set empty
    manualEdit.Status = manualSave.status;
    manualEdit.CoverImg = this.coverImage; // Use the potentially updated cover image path
    manualEdit.AppManualId = this.manual.AppManualId; // Preserve existing AppManualId

    // Determine LevelId and LevelKey based on selected business or portal
    if (manualSave.business) {
      manualEdit.LevelId = 3; // Business Level
      manualEdit.LevelKey = manualSave.business;
    } else if (manualSave.portal) {
      manualEdit.LevelId = 2; // Portal Level
      manualEdit.LevelKey = manualSave.portal;
    } else {
      manualEdit.LevelId = 1; // Common/Application Level
      manualEdit.LevelKey = BaseServices.ApplicationId; // Assuming this is the key for common
    }

    // --- Determine if Add or Update ---
    if (manualEdit.Id !== 0) {
      // --- UPDATE existing manual ---
      manualEdit.ModifiedBy = BaseServices.UserId; // Set modifier ID
      manualEdit.ModifiedOn = new Date().toISOString(); // Set modification timestamp

      let updateSubscription: Subscription;

      if (manualSave.business) {
        // Update Business Manual
        updateSubscription = this.manualService
          .updateBusinessManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result) {
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              }
            },
            error => console.error('Error updating business manual:', error)
          );
      } else if (manualSave.portal) {
        // Update Portal Manual
        updateSubscription = this.manualService
          .updatePortalManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result) {
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              }
            },
            error => console.error('Error updating portal manual:', error)
          );
      } else {
        // Update Common Manual
        updateSubscription = this.manualService
          .updateCommonManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result) {
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              }
            },
            error => console.error('Error updating common manual:', error)
          );
      }
      this.subscriptions.push(updateSubscription);
    } else {
      // --- ADD new manual ---
      manualEdit.CreatedBy = BaseServices.UserId; // Set creator ID
      manualEdit.CreatedOn = new Date().toISOString(); // Set creation timestamp
      manualEdit.AppManualId = 0; // Explicitly set for new manual
      manualEdit.ApplicationId = BaseServices.ApplicationId; // Set ApplicationId

      let addSubscription: Subscription;

      if (manualSave.business) {
        // Add Business Manual
        addSubscription = this.manualService
          .addBusinessManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result && result.Id) {
                manualEdit.Id = result.Id; // Get the new ID from the response

                // manualEdit.LevelId = 1;
                // manualEdit.LevelKey = 1;
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              } // Add error handling if needed
            },
            error => console.error('Error adding business manual:', error)
          );
      } else if (manualSave.portal) {
        // Add Portal Manual
        addSubscription = this.manualService
          .addPortalManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result && result.Id) {
                manualEdit.Id = result.Id; // Get the new ID

                // manualEdit.LevelId = 1;
                // manualEdit.LevelKey = 1;
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              } // Add error handling if needed
            },
            error => console.error('Error adding portal manual:', error)
          );
      } else {
        // Add Common Manual
        addSubscription = this.manualService
          .addCommonManual(manualEdit)
          .subscribe(
            (result: any) => {
              if (result && result.Id) {
                manualEdit.Id = result.Id; // Get the new ID

                // manualEdit.LevelId = 1;
                // manualEdit.LevelKey = 1;
                this.onClose.emit(this.closePopUp(true, manualEdit, proceed));
              } // Add error handling if needed
            },
            error => console.error('Error adding common manual:', error)
          );
      }
      this.subscriptions.push(addSubscription);
    }
  }

  /**
   * Handles the file selection event for the cover image.
   * Initiates the upload process using HttpClient.
   * NOTE: The original code had an unused onUpload method, this seems to be the active one.
   * @param event - The file input change event object.
   */
  filesSelectForCoverImage(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log('No file selected.');
      if (event?.target) event.target.value = null;
      return;
    }

    this.selectedFiles = files[0];
    this.uploadStartForCoverImage = true;
    this.showSaveAndClose = false;
    this.emitButtonState();

    let formData = new FormData();
    formData.append('file', this.selectedFiles, this.selectedFiles.name);

    const manualIdForUpload = this.manual?.Id || 0;
    const uploadUrl = `${environment.BASE_URL}/file/upload/manual/coverImage/${BaseServices.ApplicationId}/${manualIdForUpload}`;
    this.loading = true;

    this.subscriptions.push(
      this.http.post(uploadUrl, formData).subscribe(
        (response: any) => {
          if (response && response.path) {
            this.coverImage = response.path;
            console.log('Upload successful, path:', this.coverImage);
            this.showPrimeToast('success', 'Upload', 'Cover image uploaded successfully');
          } else {
            console.error('Upload response did not contain path:', response);
          }
          this.uploadStartForCoverImage = false;
          this.loading = false;
          this.showSaveAndClose = true;
          this.emitButtonState();
        },
        error => {
          console.error('Upload manual cover image Error : ', error);
          this.uploadStartForCoverImage = false;
          this.loading = false;
          this.showSaveAndClose = true;
          this.emitButtonState();

          this.translate
            .get('COVER_IMAGE_UPLOAD_FAILED')
            .subscribe((res: string) => {
              this.showPrimeToast('error', 'Upload Error', res + (error.message ? `\n${error.message}` : ''));
            });
        }
      )
    );
    if (event?.target) event.target.value = null;
  }
  /**
   * Helper method to emit the current button state.
   */
  private emitButtonState() {
    this.buttonStateChange.emit({
      showSaveAndClose: this.showSaveAndClose,
      hideSaveandcontinue: this.hideSaveandcontinue
    });
  }

  /**
   * Helper method to create toast message (convert to use PrimeNG)
   * @param manualEdit - The ManualEdit object containing the saved manual's data.
   */
  popToastMessage(manualEdit: ManualEdit): void {
    if (manualEdit && manualEdit.Title) {
      let modeKey = this.manual && this.manual.Id > 0 ? 'updated' : 'created';
      let bodyKey = 'MANUAL_SAVED_SUCCESS';

      let titleStr = '';
      let bodyStr = '';
      this.translate
        .get('ADD_UPDATE_MANUAL_TITLE')
        .subscribe(res => (titleStr = res));
      this.translate
        .get(bodyKey, {
          title: manualEdit.Title,
          mode: this.translate.instant(modeKey)
        })
        .subscribe(res => (bodyStr = res));

      this.showPrimeToast(
        'success',
        titleStr || 'Add/Update Manual',
        bodyStr || `${manualEdit.Title} manual ${modeKey} successfully`
      );
    }
  }

  /**
   * Helper function to create the object emitted by the onClose event.
   * @param saved - Boolean indicating if the operation resulted in a save.
   * @param manualEdit - Optional. The manual data that was saved.
   * @param proceed - Optional. Boolean indicating if the user chose 'Save & Continue'.
   * @returns An object structured for the onClose event emitter.
   */
  closePopUp(saved: boolean, manualEdit?: ManualEdit, proceed?: boolean) {
    // Show toast if saved
    if (saved && manualEdit) {
      this.popToastMessage(manualEdit); // Call the new toast method
    }

    let returnObj = {
      isSaved: saved,
      isProceed: saved ? proceed : false,
      toast: null,
      manual: saved ? manualEdit : null
    };
    return returnObj;
  }

  /**
   * Custom validator function to check if a form control value consists only of whitespace.
   * @param control - The form control (AbstractControl) to validate.
   * @returns An error object { whitespace: true } if invalid, otherwise null.
   */
  public noWhitespaceValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    // Check if control and value exist, trim it, and check length
    const isWhitespace =
      control && control.value && typeof control.value === 'string'
        ? control.value.trim().length === 0
        : false;
    // The control is valid if it's not whitespace
    const isValid = !isWhitespace;
    // Return null if valid, or an error object if invalid
    return isValid ? null : { whitespace: true };
  }

  /**
   * Helper method to display PrimeNG Toast
   */
  showPrimeToast(severity: string, summary: string, detail: string) {
    this.messageService.add({
      severity,
      summary,
      detail,
      life: 3000
    });
  }

  /**
   * Lifecycle hook called just before the component is destroyed.
   * Unsubscribes from all stored RxJS subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
    this.subscriptions = [];
  }
}
